import React, { useState, useEffect } from 'react';
import api from '../services/api';

interface Documento {
  id: number;
  id_analisis: number;
  nombre: string;
  descripcion: string;
}

interface DocumentosModalProps {
  analisisId: number;
  isOpen: boolean;
  onClose: () => void;
}

const DocumentosModal: React.FC<DocumentosModalProps> = ({ analisisId, isOpen, onClose }) => {
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [descripcion, setDescripcion] = useState('');
  const [uploadStatus, setUploadStatus] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'ver' | 'cargar'>('ver');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentoToDelete, setDocumentoToDelete] = useState<number | null>(null);

  // Cargar documentos cuando se abre el modal
  useEffect(() => {
    if (isOpen && analisisId) {
      loadDocumentos();
    }
  }, [isOpen, analisisId]);

  const loadDocumentos = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/documentos/${analisisId}`);
      setDocumentos(response.data);
    } catch (error) {
      console.error('Error al cargar documentos:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Validar que sea un archivo PDF
      if (file.type !== 'application/pdf') {
        setUploadStatus('Error: Solo se permiten archivos PDF');
        return;
      }
      
      // Validar tamaño máximo (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setUploadStatus('Error: El archivo excede el límite de 5MB');
        return;
      }
      
      setSelectedFile(file);
      setUploadStatus(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadStatus('Por favor, seleccione un archivo PDF');
      return;
    }

    try {
      setIsLoading(true);
      setUploadStatus('Subiendo documento...');

      const formData = new FormData();
      formData.append('documento', selectedFile);
      formData.append('descripcion', descripcion);

      await api.post(`/documentos/${analisisId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setUploadStatus('Documento subido correctamente');
      setSelectedFile(null);
      setDescripcion('');
      
      // Recargar la lista de documentos
      loadDocumentos();
      
      // Cambiar a la pestaña de ver documentos
      setActiveTab('ver');
    } catch (error) {
      console.error('Error al subir documento:', error);
      setUploadStatus('Error al subir el documento');
    } finally {
      setIsLoading(false);
    }
  };

  const handleView = async (documentoId: number) => {
    try {
      // Se pide el PDF autenticado por header (no por query string) y se abre como blob local
      const response = await api.get(`/documentos/ver/${documentoId}`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      window.open(blobUrl, '_blank');
      // Liberar el objeto luego de un momento, una vez que la pestaña ya lo cargó
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (error) {
      console.error('Error al abrir el documento:', error);
      alert('No se pudo abrir el documento. Intente nuevamente.');
    }
  };

  // Modificar la función handleDelete
  const handleDelete = async (documentoId: number) => {
    // En lugar de usar confirm, mostramos nuestro modal personalizado
    setDocumentoToDelete(documentoId);
    setShowDeleteConfirm(true);
  };

  // Agregar una nueva función para confirmar la eliminación
  const confirmDelete = async () => {
    if (!documentoToDelete) return;
    
    try {
      setIsLoading(true);
      await api.delete(`/documentos/${documentoToDelete}`);
      
      // Actualizar la lista de documentos
      loadDocumentos();
      
    } catch (error) {
      console.error('Error al eliminar documento:', error);
      alert('Error al eliminar el documento');
    } finally {
      setIsLoading(false);
      setShowDeleteConfirm(false);
      setDocumentoToDelete(null);
    }
  };

  // Si el modal no está abierto, no renderizar nada
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Documentos del Análisis</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button 
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'ver' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('ver')}
            >
              Ver Documentos
            </button>
            <button 
              className={`px-4 py-2 text-sm font-medium ${activeTab === 'cargar' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('cargar')}
            >
              Cargar Documento
            </button>
          </div>
        </div>
        
        <div className="p-4">
          {/* Contenido de la pestaña "Ver Documentos" */}
          {activeTab === 'ver' && (
            <div>
              {isLoading ? (
                <div className="text-center py-10">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Cargando documentos...</p>
                </div>
              ) : documentos.length > 0 ? (
                <div className="overflow-auto max-h-96">
                  <table className="min-w-full bg-white">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                        <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {documentos.map((doc) => (
                        <tr key={doc.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{doc.nombre}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{doc.descripcion || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                            <div className="flex justify-center space-x-2">
                              <button
                                onClick={() => handleView(doc.id)}
                                className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full hover:bg-blue-200"
                              >
                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                </svg>
                                Ver
                              </button>
                              
                              <button
                                onClick={() => handleDelete(doc.id)}
                                className="inline-flex items-center px-3 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full hover:bg-red-200"
                              >
                                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                  <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Eliminar
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-10">
                  <svg className="w-12 h-12 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="mt-2 text-gray-600">No hay documentos disponibles para este análisis</p>
                </div>
              )}
            </div>
          )}
          
          {/* Contenido de la pestaña "Cargar Documento" */}
          {activeTab === 'cargar' && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-md p-6 text-center">
                {selectedFile ? (
                  <div className="text-center">
                    <svg className="mx-auto h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="mt-2 text-sm font-medium text-green-600">{selectedFile.name}</p>
                    <p className="text-xs text-gray-500">{Math.round(selectedFile.size / 1024)} KB</p>
                    <button 
                      onClick={() => setSelectedFile(null)}
                      className="mt-2 px-3 py-1 text-xs text-red-600 hover:text-red-800"
                    >
                      Eliminar
                    </button>
                  </div>
                ) : (
                  <div>
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="mt-2 text-sm text-gray-600">
                      <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500">
                        <span>Subir un archivo</span>
                        <input id="file-upload" name="file-upload" type="file" accept="application/pdf" className="sr-only" onChange={handleFileChange} />
                      </label>
                    </p>
                    <p className="text-xs text-gray-500">PDF hasta 5MB</p>
                  </div>
                )}
              </div>
              
              <div>
                <label htmlFor="descripcion" className="block text-sm font-medium text-gray-700">Descripción (opcional)</label>
                <textarea
                  id="descripcion"
                  name="descripcion"
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Ingrese una descripción para el documento"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                ></textarea>
              </div>
              
              {uploadStatus && (
                <div className={`text-sm ${uploadStatus.startsWith('Error') ? 'text-red-600' : uploadStatus === 'Documento subido correctamente' ? 'text-green-600' : 'text-blue-600'}`}>
                  {uploadStatus}
                </div>
              )}
              
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleUpload}
                  disabled={isLoading || !selectedFile}
                  className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${isLoading || !selectedFile ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                >
                  {isLoading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <svg className="-ml-1 mr-2 h-4 w-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                      Subir documento
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmación para eliminar documento */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-auto">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirmar eliminación</h3>
            <p className="text-sm text-gray-500 mb-4">¿Está seguro que desea eliminar este documento? Esta acción no se puede deshacer.</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="px-3 py-1.5 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentosModal; 